import { useGraphQLQuery } from '../../../hooks/useGraphQLRequest';
import { type ResultOf } from '@graphql-typed-document-node/core';
import { CurrentUserGql, EmergencyContactGql, StatesGql } from './graphql';
import { OptionType } from '@medely/ui-kit';
import { useMemo } from 'react';

export const usePersonalFormData = () => {
  const { data, isLoading: statesLoading } = useGraphQLQuery<ResultOf<typeof StatesGql>>({
    operationName: 'GetStatesForPersonalForm',
    query: StatesGql,
    variables: { input: { orderBy: { name: true } } },
  });

  const states = data?.states ?? [];

  const stateOptions: OptionType[] = useMemo(
    () => states.map(({ id, name }) => ({ value: id, label: name ? name : '' })),
    [states],
  );

  const { data: currentUserData, isLoading: currentUserLoading } = useGraphQLQuery<
    ResultOf<typeof CurrentUserGql>
  >({
    operationName: 'GetCurrentUserForPersonalForm',
    query: CurrentUserGql,
    variables: { input: { orderBy: { name: true } } },
  });

  const currentUser = currentUserData?.me;

  const { data: emergencyContactData, isLoading: emergencyContactLoading } = useGraphQLQuery<
    ResultOf<typeof EmergencyContactGql>
  >({
    operationName: 'GetEmergencyContactForPersonalForm',
    query: EmergencyContactGql,
    variables: { input: { filter: { professional_id: currentUser?.professional?.id } } },
  });

  const emergencyContacts = emergencyContactData?.emergencyContacts;

  const proEmergencyContact = emergencyContacts
    ? {
        emergency_first_name: emergencyContacts[0]?.first_name,
        emergency_last_name: emergencyContacts[0]?.last_name,
        emergency_phone_number: emergencyContacts[0]?.phone_number,
        emergency_id: emergencyContacts[0]?.id,
      }
    : {};

  const defaultValues = {
    nickname: currentUser?.professional?.nickname,
    ...currentUser,
    ...proEmergencyContact,
  };

  const loadingData = statesLoading || currentUserLoading || emergencyContactLoading;

  return {
    stateOptions,
    defaultValues,
    loadingData,
  };
};
